import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { canManageBilling, type Contact, type Organization, type TicketSummary } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { BlockHoursPanel } from '../components/BlockHoursPanel.tsx';
import { BlockHoursBillingCard } from '../components/BlockHoursBillingCard.tsx';
import { Button } from '../components/Button.tsx';
import { Card, StatusBadge, TypeBadge } from '../components/Card.tsx';
import { CheckInsPanel } from '../components/CheckInsPanel.tsx';
import { DeleteCascadeDialog } from '../components/DeleteCascadeDialog.tsx';
import { FormField } from '../components/FormField.tsx';
import { OrgAttachmentsPanel } from '../components/OrgAttachmentsPanel.tsx';
import { OrgProfileCard } from '../components/OrgProfileCard.tsx';

interface OrgDeletePreview {
  organization: { id: string; name: string };
  counts: {
    contacts: number;
    tickets: number;
    ticketsOpen: number;
    projects: number;
    projectsActive: number;
    blockHourSubscriptions: number;
    blockHourSubscriptionsActive: number;
    blockHourPeriods: number;
    blockHourPurchases: number;
    purchasedHours: number;
    checkIns: number;
    attachments: number;
    timeEntries: number;
    totalMinutes: number;
    apiKeys: number;
    webhookEndpoints: number;
  };
}

export function OrganizationDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const [org, setOrg] = useState<Organization | null>(null);
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletePreview, setDeletePreview] = useState<OrgDeletePreview | null>(null);
  const [deletePreviewBusy, setDeletePreviewBusy] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isRequester, setIsRequester] = useState(true);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isOrgBilling, setIsOrgBilling] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState<string | null>(null);

  async function load() {
    try {
      const [orgRes, contactsRes, ticketsRes] = await Promise.all([
        apiFetch<{ organization: Organization }>(`/api/organizations/${id}`),
        apiFetch<{ contacts: Contact[] }>(`/api/organizations/${id}/contacts`),
        apiFetch<{ tickets: TicketSummary[] }>(`/api/tickets?organizationId=${id}&limit=25`),
      ]);
      setOrg(orgRes.organization);
      setContacts(contactsRes.contacts);
      setTickets(ticketsRes.tickets);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load organization.');
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  async function addContact(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/organizations/${id}/contacts`, {
        method: 'POST',
        json: {
          email: email.trim(),
          name: name.trim() || null,
          isRequester,
          isOrgAdmin,
          isOrgBilling,
        },
      });
      setEmail('');
      setName('');
      setIsRequester(true);
      setIsOrgAdmin(false);
      setIsOrgBilling(false);
      setAdding(false);
      await load();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to add contact.');
    } finally {
      setBusy(false);
    }
  }

  async function resetMfa(contactId: string, contactEmail: string) {
    if (
      !window.confirm(
        `Reset MFA for ${contactEmail}? This wipes their authenticator + backup codes and issues a new enrollment link. They will be locked out until they re-enroll.`,
      )
    ) {
      return;
    }
    setResetBusy(contactId);
    setInviteInfo(null);
    try {
      const res = await apiFetch<{ enrollmentUrl: string; emailSent: boolean; emailError: string | null; expiresInHours: number }>(
        `/api/organizations/${id}/contacts/${contactId}/reset-mfa`,
        { method: 'POST' },
      );
      const lines = [
        `MFA reset for ${contactEmail}. New enrollment URL:`,
        res.enrollmentUrl,
        `(Expires in ${res.expiresInHours}h.)`,
      ];
      if (res.emailSent) lines.push('Email also sent.');
      else lines.push(`Email not sent (${res.emailError ?? 'no SMTP config'}). Share the URL above manually.`);
      setInviteInfo(lines.join('\n'));
      await load();
    } catch (err) {
      setInviteInfo(err instanceof ApiError ? err.message : 'Failed to reset MFA.');
    } finally {
      setResetBusy(null);
    }
  }

  async function resetPassword(contactId: string, contactEmail: string) {
    if (!window.confirm(`Email a password-reset link to ${contactEmail}?`)) return;
    setResetBusy(contactId);
    setInviteInfo(null);
    try {
      await apiFetch(`/api/organizations/${id}/contacts/${contactId}/reset-password`, {
        method: 'POST',
      });
      setInviteInfo(`Password reset link sent to ${contactEmail} (valid 1h).`);
    } catch (err) {
      setInviteInfo(err instanceof ApiError ? err.message : 'Failed to send password reset.');
    } finally {
      setResetBusy(null);
    }
  }

  async function sendPortalInvite(contactId: string, contactEmail: string) {
    setInviteBusy(contactId);
    setInviteInfo(null);
    try {
      const res = await apiFetch<{ enrollmentUrl: string; emailSent: boolean; emailError: string | null; expiresInHours: number }>(
        `/api/organizations/${id}/contacts/${contactId}/portal-invite`,
        { method: 'POST' },
      );
      const lines = [
        `Portal invite for ${contactEmail}:`,
        res.enrollmentUrl,
        `(Expires in ${res.expiresInHours}h.)`,
      ];
      if (res.emailSent) lines.push('Email also sent.');
      else lines.push(`Email not sent (${res.emailError ?? 'no SMTP config'}). Share the URL above manually.`);
      setInviteInfo(lines.join('\n'));
      await load();
    } catch (err) {
      setInviteInfo(err instanceof ApiError ? err.message : 'Failed to send invite.');
    } finally {
      setInviteBusy(null);
    }
  }

  async function openDeleteDialog() {
    setDeletePreviewError(null);
    setDeletePreviewBusy(true);
    try {
      const res = await apiFetch<OrgDeletePreview>(`/api/organizations/${id}/delete-preview`);
      setDeletePreview(res);
    } catch (err) {
      setDeletePreviewError(err instanceof ApiError ? err.message : 'Failed to load preview.');
    } finally {
      setDeletePreviewBusy(false);
    }
  }

  async function confirmDeleteOrg(confirmName: string) {
    await apiFetch(`/api/organizations/${id}`, {
      method: 'DELETE',
      json: { confirmName },
    });
    navigate('/organizations');
  }

  if (loadError) return <p className="text-sm text-[var(--color-danger)]">{loadError}</p>;
  if (!org || !contacts || !tickets) return <p className="text-[var(--color-muted)]">Loading…</p>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/organizations" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Organizations
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-[var(--color-header)]">{org.name}</h1>
          {org.huduCompanyUrl && (
            <a
              href={org.huduCompanyUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
              title="Open this client's Hudu company page in a new tab"
            >
              Open in Hudu →
            </a>
          )}
        </div>
        <p className="text-[var(--color-muted)]">
          {org.domain ?? 'No domain set'} · {org.timezone}
        </p>
      </div>

      <OrgProfileCard org={org} onSaved={load} canManageBilling={canManageBilling(agent?.role)} />

      <OrgAttachmentsPanel orgId={org.id} />

      <BlockHoursPanel orgId={org.id} isAdmin={isAdmin} />
      {canManageBilling(agent?.role) && <BlockHoursBillingCard organizationId={org.id} />}

      <CheckInsPanel
        org={{
          id: org.id,
          name: org.name,
          checkinCadence: org.checkinCadence ?? null,
          checkinDayOfWeek: org.checkinDayOfWeek ?? null,
          checkinTime: org.checkinTime ?? null,
        }}
      />

      {isAdmin && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                Outbound webhooks
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted)]">
                When enabled, this organization's admin contacts can configure their own
                webhook endpoints in the portal to receive signed pushes for time-entry
                events ({org.webhooksEnabled ? 'enabled' : 'disabled'}).
              </p>
            </div>
            <Button
              variant={org.webhooksEnabled ? 'outline' : 'primary'}
              onClick={async () => {
                try {
                  await apiFetch(`/api/organizations/${id}`, {
                    method: 'PATCH',
                    json: { webhooksEnabled: !org.webhooksEnabled },
                  });
                  await load();
                } catch (err) {
                  setLoadError(err instanceof ApiError ? err.message : 'Failed to update.');
                }
              }}
            >
              {org.webhooksEnabled ? 'Disable webhooks' : 'Enable webhooks'}
            </Button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">Contacts</h2>
        <Button onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add contact'}</Button>
      </div>

      {adding && (
        <Card>
          <form onSubmit={addContact} className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FormField label="Name" name="name" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="sm:col-span-2">
              <span className="mb-2 block text-sm font-medium text-[var(--color-header)]">Roles</span>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isRequester}
                    onChange={(e) => setIsRequester(e.target.checked)}
                    className="h-4 w-4 accent-[var(--color-t3-blue)]"
                  />
                  Requester (can submit and view their tickets)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isOrgAdmin}
                    onChange={(e) => setIsOrgAdmin(e.target.checked)}
                    className="h-4 w-4 accent-[var(--color-t3-blue)]"
                  />
                  Org admin (can manage other contacts)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isOrgBilling}
                    onChange={(e) => setIsOrgBilling(e.target.checked)}
                    className="h-4 w-4 accent-[var(--color-t3-blue)]"
                  />
                  Org billing (sees hours and invoices)
                </label>
              </div>
            </div>
            {createError && <p className="text-sm text-[var(--color-danger)] sm:col-span-2">{createError}</p>}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy || !email.trim()}>
                {busy ? 'Adding…' : 'Add contact'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {inviteInfo && (
        <Card className="border-[var(--color-t3-blue)]/30 bg-blue-50/40">
          <pre className="whitespace-pre-wrap break-all text-xs text-[var(--color-body)]">{inviteInfo}</pre>
        </Card>
      )}

      {contacts.length === 0 ? (
        <Card>
          <p className="text-center text-[var(--color-muted)]">No contacts yet. Add one to set them as a ticket requester.</p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Roles</th>
                <th className="px-4 py-3 text-center" title="See every ticket for this org in the portal">View all</th>
                <th className="px-4 py-3 text-center" title="CC this email on every ticket via outbound replies">CC all</th>
                <th className="px-4 py-3">Portal</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3 font-mono text-xs">{c.email}</td>
                  <td className="px-4 py-3">{c.name ?? '—'}</td>
                  <td className="px-4 py-3">
                    <ContactRoleCheckboxes contact={c} orgId={id} onChanged={load} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ContactToggle contact={c} orgId={id} field="canViewAllOrgTickets" onChanged={load} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ContactToggle contact={c} orgId={id} field="subscribeCcAllTickets" onChanged={load} />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.totpEnrolledAt ? (
                      <span className="text-green-700">enrolled</span>
                    ) : c.isPortalUser ? (
                      <span className="text-amber-700">invited</span>
                    ) : (
                      <span className="text-[var(--color-muted)]">none</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!c.totpEnrolledAt ? (
                      <Button
                        variant="outline"
                        onClick={() => void sendPortalInvite(c.id, c.email)}
                        disabled={inviteBusy === c.id}
                      >
                        {inviteBusy === c.id ? 'Sending…' : c.isPortalUser ? 'Re-send invite' : 'Invite'}
                      </Button>
                    ) : isAdmin ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => void resetPassword(c.id, c.email)}
                          disabled={resetBusy === c.id}
                        >
                          {resetBusy === c.id ? 'Working…' : 'Reset password'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => void resetMfa(c.id, c.email)}
                          disabled={resetBusy === c.id}
                        >
                          {resetBusy === c.id ? 'Working…' : 'Reset MFA'}
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">Recent tickets</h2>
        <Link
          to={`/tickets/new`}
          className="text-sm text-[var(--color-t3-blue)] hover:underline"
        >
          + New ticket
        </Link>
      </div>

      {tickets.length === 0 ? (
        <Card>
          <p className="text-center text-[var(--color-muted)]">No tickets yet for this organization.</p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3 font-mono text-xs">T-{t.displayNumber}</td>
                  <td className="px-4 py-3"><TypeBadge type={t.type} /></td>
                  <td className="px-4 py-3">
                    <Link to={`/tickets/${t.id}`} className="text-[var(--color-t3-blue)] hover:underline">
                      {t.subject}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={t.status}>{t.status.replace(/_/g, ' ')}</StatusBadge></td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {new Date(t.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <Card className="border-[var(--color-danger)]/40">
          <h2 className="text-base font-semibold text-[var(--color-danger)]">Danger zone</h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted)]">
            Deleting <strong>{org.name}</strong> permanently removes every ticket, project,
            time entry, block-hour record, check-in, contact, attachment, and API key for this
            client. Billing history (purchases, periods, consumptions) is destroyed. This cannot
            be undone — typically you want to mark the org inactive instead.
          </p>
          {deletePreviewError && (
            <p className="mt-2 text-sm text-[var(--color-danger)]">{deletePreviewError}</p>
          )}
          <Button
            variant="danger"
            className="mt-3"
            onClick={() => void openDeleteDialog()}
            disabled={deletePreviewBusy}
          >
            {deletePreviewBusy ? 'Loading…' : 'Delete organization…'}
          </Button>
        </Card>
      )}

      {deletePreview && (
        <DeleteCascadeDialog
          title={`Delete organization "${deletePreview.organization.name}"?`}
          intro="This permanently removes the organization and every record attached to it. This is irreversible — the operation runs in a single transaction so a partial failure rolls back, but a successful delete cannot be undone."
          warnings={
            deletePreview.counts.blockHourSubscriptionsActive > 0
              ? [
                  `This org has ${deletePreview.counts.blockHourSubscriptionsActive} active block-hour subscription${deletePreview.counts.blockHourSubscriptionsActive === 1 ? '' : 's'}. Billing history will be destroyed.`,
                ]
              : undefined
          }
          willBeRemoved={[
            {
              label: 'Tickets',
              count: deletePreview.counts.tickets,
              sublabel:
                deletePreview.counts.ticketsOpen > 0
                  ? `${deletePreview.counts.ticketsOpen} still open — messages, attachments, CSAT, Zendesk/Harvest maps all go with them`
                  : 'with all messages, attachments, CSAT surveys, and import maps',
            },
            {
              label: 'Time entries',
              count: deletePreview.counts.timeEntries,
              sublabel:
                deletePreview.counts.totalMinutes > 0
                  ? `${(deletePreview.counts.totalMinutes / 60).toFixed(2)}h logged — block-hour consumption records also removed`
                  : undefined,
            },
            {
              label: 'Projects',
              count: deletePreview.counts.projects,
              sublabel:
                deletePreview.counts.projectsActive > 0
                  ? `${deletePreview.counts.projectsActive} active — subtasks and milestones also deleted`
                  : 'with all subtasks and milestones',
            },
            {
              label: 'Block-hour subscriptions',
              count: deletePreview.counts.blockHourSubscriptions,
              sublabel:
                deletePreview.counts.blockHourSubscriptionsActive > 0
                  ? `${deletePreview.counts.blockHourSubscriptionsActive} active or cancellation-pending`
                  : undefined,
            },
            {
              label: 'Block-hour periods (monthly buckets)',
              count: deletePreview.counts.blockHourPeriods,
            },
            {
              label: 'Block-hour purchases (ad-hoc top-ups)',
              count: deletePreview.counts.blockHourPurchases,
              sublabel:
                deletePreview.counts.purchasedHours > 0
                  ? `${deletePreview.counts.purchasedHours.toFixed(2)}h purchased lifetime`
                  : undefined,
            },
            { label: 'Contacts (with portal logins, MFA, backup codes)', count: deletePreview.counts.contacts },
            { label: 'Check-ins', count: deletePreview.counts.checkIns },
            { label: 'Org attachments', count: deletePreview.counts.attachments },
            { label: 'API keys', count: deletePreview.counts.apiKeys },
            { label: 'Webhook endpoints (with delivery history)', count: deletePreview.counts.webhookEndpoints },
          ]}
          confirmName={deletePreview.organization.name}
          confirmButtonLabel="Delete organization permanently"
          onConfirm={() => confirmDeleteOrg(deletePreview.organization.name)}
          onClose={() => setDeletePreview(null)}
        />
      )}
    </div>
  );
}

function ContactRoleCheckboxes({
  contact,
  orgId,
  onChanged,
}: {
  contact: Contact;
  orgId: string;
  onChanged: () => Promise<void> | void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  async function patch(field: 'isRequester' | 'isOrgAdmin' | 'isOrgBilling', value: boolean) {
    setBusy(true);
    try {
      await apiFetch(`/api/organizations/${orgId}/contacts/${contact.id}`, {
        method: 'PATCH',
        json: { [field]: value },
      });
      await onChanged();
    } finally {
      setBusy(false);
    }
  }
  const cellLabel = 'flex items-center gap-1 text-xs whitespace-nowrap';
  return (
    <div className="flex flex-col gap-1">
      <label className={cellLabel}>
        <input
          type="checkbox"
          checked={contact.isRequester}
          disabled={busy}
          onChange={(e) => void patch('isRequester', e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-t3-blue)]"
        />
        Requester
      </label>
      <label className={cellLabel}>
        <input
          type="checkbox"
          checked={contact.isOrgAdmin}
          disabled={busy}
          onChange={(e) => void patch('isOrgAdmin', e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-t3-blue)]"
        />
        Org admin
      </label>
      <label className={cellLabel}>
        <input
          type="checkbox"
          checked={contact.isOrgBilling}
          disabled={busy}
          onChange={(e) => void patch('isOrgBilling', e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-t3-blue)]"
        />
        Org billing
      </label>
    </div>
  );
}

function ContactToggle({
  contact,
  orgId,
  field,
  onChanged,
}: {
  contact: Contact;
  orgId: string;
  field: 'canViewAllOrgTickets' | 'subscribeCcAllTickets';
  onChanged: () => Promise<void> | void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const value = contact[field];
  async function toggle() {
    setBusy(true);
    try {
      await apiFetch(`/api/organizations/${orgId}/contacts/${contact.id}`, {
        method: 'PATCH',
        json: { [field]: !value },
      });
      await onChanged();
    } finally {
      setBusy(false);
    }
  }
  return (
    <input
      type="checkbox"
      checked={value}
      onChange={() => void toggle()}
      disabled={busy}
      className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
    />
  );
}
